import { Button, Icon, Modal, Spinner, Typography } from "@/components/atoms";
import { ClientPaymentMethod } from "@/redux/apis/clientPayment/types";
import { PaymentMethodRow } from "./summary/PaymentMethodRow";

interface ConfirmCardModalProps {
  open: boolean;
  paymentMethod: ClientPaymentMethod;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Final "is this the right card?" gate before a card-paid booking is created
 * (web-only — payments sent to the wrong card can't be refunded). Invoice
 * bookings skip this modal entirely.
 */
export const ConfirmCardModal = ({ open, paymentMethod, onClose, onConfirm, loading = false }: ConfirmCardModalProps) => (
  <Modal
    open={open}
    onClose={loading ? () => undefined : onClose}
    variant="web-small"
    // Compact confirmation — pin to 400px on sm+ (mobile stays fluid so it never overflows a small screen).
    className="sm:w-[400px]"
    title="Confirm your payment card"
    description="You're about to book this trip with the card below."
    renderButtons={() => (
      <>
        <Button variant="tertiary" size="lg" onClick={onClose} disabled={loading} className="flex-1">
          Go Back
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onConfirm}
          disabled={loading}
          className="flex-1"
          startIcon={loading ? <Spinner variant="light" /> : undefined}
        >
          Confirm &amp; Book
        </Button>
      </>
    )}
  >
    <div className="flex flex-col gap-2">
      <PaymentMethodRow method={paymentMethod} variant="boxed" showDefaultBadge />

      <div className="flex items-center gap-2 rounded-md border border-warning bg-warning-light px-2 py-1">
        <Icon name="Danger" size="lg" className="shrink-0 text-warning" />
        <div className="min-w-0">
          <Typography variant="small" className="block">
            Double-check your card before booking.
          </Typography>
          <Typography variant="small-regular" className="block">
            Payments sent to the wrong card can&apos;t be refunded or reversed.
          </Typography>
        </div>
      </div>
    </div>
  </Modal>
);
